import React, { useState, useEffect } from 'react';
import { Flex, Text, Toggle, Dialog, Dialogs, Toast, Label, Content, Radio } from '@leverege/ui-elements';
import classnames from 'classnames';
import { GlobalState } from '@leverege/ui-redux';
import Molten from '@leverege/molten';
import { updateSimOTAActive, updateSimOTAEnabledForAllLocations } from '../../../util/Interface';
import S from './SimOTACard.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const SimOTACard = props => {
  const {
    sharedProps: {
      item,
      actions
    }
  } = props;
  const systemId = Molten.getConfig('api/systemId');
  let system;
  const [active, setActive] = useState(false);
  const [loadingActive, setLoadingActive] = useState(true);
  const [enabledAllLocations, setEnabledAllLocations] = useState(false);
  const [loadingEnabledAllLocations, setLoadingEnabledAllLocations] = useState(true);

  // Don't need to default to "loading" because location device is already available
  const [enabledThisLocation, setEnabledThisLocation] = useState(!!item?.data?.simOTAEnabled);
  const [loadingEnabledThisLocation, setLoadingEnabledThisLocation] = useState(false);
  useEffect(() => {
    GlobalState.dispatch(async (dispatch, getState, {
      api
    }) => {
      system = await api.system(systemId).get();
      setEnabledAllLocations(system?.metadata?.simOTAEnabledAllLocations ? 'true' : 'false');
      setLoadingEnabledAllLocations(false);
      setActive(!!system?.metadata?.simOTAActive);
      setLoadingActive(false);
    });
  }, []);
  const toggleActive = async newValue => {
    setLoadingActive(true);
    try {
      await updateSimOTAActive(systemId, newValue);
      setActive(newValue);
    } finally {
      // catch error message is already done inside the utility function
      setLoadingActive(false);
    }
  };
  const toggleActiveWarning = switchClickEvent => {
    const {
      value: newValue
    } = switchClickEvent;
    Dialogs.show({
      component: props => {
        const {
          show,
          onClose,
          toggleActive: pToggleActive
        } = props;
        const onOkay = buttonClickEvent => {
          pToggleActive(newValue);
          onClose();
        };
        const activationWarningText = newValue ? 'Activate the SIM OTA feature? This will allow SIM OTA to be enabled at any location. ' + 'Locators will not receive SIM OTA commands until their parent location has been enabled.' : 'Deactivate the SIM OTA feature? Deactivating will reset the SIM OTA setting to \'disabled\' ' + 'for every location in the system.';
        const activateVerb = newValue ? 'Activate' : 'Deactivate';
        return /*#__PURE__*/_jsx(Dialog.Question, {
          show: show,
          onClose: onClose,
          title: `${activateVerb} SIM OTA`,
          okay: activateVerb,
          okayVariant: "primary",
          cancelVariant: "secondary",
          onOkay: onOkay,
          onCancel: onClose,
          children: activationWarningText
        });
      },
      props: {
        toggleActive
      }
    });
  };
  const toggleEnabledAllLocations = async newValue => {
    const valueToSave = newValue === 'true';
    setLoadingEnabledAllLocations(true);
    try {
      await updateSimOTAEnabledForAllLocations(systemId, valueToSave);
      setEnabledAllLocations(newValue);
    } finally {
      // catch error message is already done inside the utility function
      setLoadingEnabledAllLocations(false);
    }
  };
  const toggleEnabledAllLocationsWarning = radioClickEvent => {
    const {
      data: newValue
    } = radioClickEvent;
    if (newValue === 'false') {
      toggleEnabledAllLocations(newValue);
      return;
    }
    Dialogs.show({
      component: props => {
        const {
          show,
          onClose,
          toggleEnabledAllLocations: pToggleEnabledAllLocations
        } = props;
        const onOkay = buttonClickEvent => {
          pToggleEnabledAllLocations(newValue);
          onClose();
        };
        const enabledWarningText = 'Enable SIM OTA for all locations? All locators will receive a SIM OTA update ' + 'whenever eligible, regardless of parent location.';
        return /*#__PURE__*/_jsx(Dialog.Question, {
          show: show,
          onClose: onClose,
          title: "Enable For All Locations",
          okay: "Enable",
          okayVariant: "primary",
          cancelVariant: "secondary",
          onOkay: onOkay,
          onCancel: onClose,
          children: enabledWarningText
        });
      },
      props: {
        toggleEnabledAllLocations
      }
    });
  };
  const toggleLocation = async () => {
    setLoadingEnabledThisLocation(true);
    try {
      await GlobalState.dispatch(actions.update({
        data: {
          simOTAEnabled: !enabledThisLocation
        }
      }));
      setEnabledThisLocation(prevValue => !prevValue);
    } catch (err) {
      Toast.error('Error updating SIM OTA enabled for this Location');
    } finally {
      setLoadingEnabledThisLocation(false);
    }
  };
  const SystemSection = () => {
    return /*#__PURE__*/_jsxs(React.Fragment, {
      children: [/*#__PURE__*/_jsxs(Content.Header, {
        className: S.header,
        children: [/*#__PURE__*/_jsx(Text, {
          variant: "sectionTitle",
          children: "System-wide settings"
        }), /*#__PURE__*/_jsx(Text, {
          variant: "sectionBody",
          children: "These settings are shared between every location in the system. Changes made here will be shown at all locations."
        })]
      }), /*#__PURE__*/_jsxs(Content.Area, {
        className: S.area,
        children: [/*#__PURE__*/_jsx(Text, {
          variant: "listItemTitle",
          children: "SIM OTA Feature Activation"
        }), /*#__PURE__*/_jsx(Text, {
          variant: "listItemSubtitle",
          children: "Activate to allow SIM OTA the feature to run on the system."
        }), /*#__PURE__*/_jsxs(Flex, {
          variant: "rowL",
          className: S.paddingTop,
          children: [/*#__PURE__*/_jsx(Toggle, {
            variant: "default",
            value: active,
            disabled: loadingActive,
            eventData: "active",
            onChange: toggleActiveWarning
          }), /*#__PURE__*/_jsx(Label, {
            variant: "inputCaption",
            className: S.label,
            children: active ? 'SIM OTA Active' : 'SIM OTA Inactive'
          })]
        })]
      }), /*#__PURE__*/_jsxs(Content.Area, {
        className: classnames(S.area, active ? '' : S.disabled),
        children: [/*#__PURE__*/_jsx(Text, {
          variant: "listItemTitle",
          children: "Enable for all locations"
        }), /*#__PURE__*/_jsx(Text, {
          variant: "listItemSubtitle",
          children: "Select whether SIM OTA should be enabled for all locations, or only for locations that have been enabled individually."
        }), /*#__PURE__*/_jsxs(Flex, {
          variant: "rowL",
          className: S.paddingTop,
          children: [/*#__PURE__*/_jsx(Radio, {
            label: "Enable locations individually",
            labelVariant: "inputCaption",
            on: "false",
            eventData: "false",
            disabled: loadingEnabledAllLocations || !active,
            value: enabledAllLocations,
            onChange: toggleEnabledAllLocationsWarning
          }), /*#__PURE__*/_jsx(Radio, {
            label: "Enable for all locations",
            labelVariant: "inputCaption",
            on: "true",
            eventData: "true",
            disabled: loadingEnabledAllLocations || !active,
            value: enabledAllLocations,
            onChange: toggleEnabledAllLocationsWarning
          })]
        })]
      })]
    });
  };
  const LocationSection = () => {
    return /*#__PURE__*/_jsxs(React.Fragment, {
      children: [/*#__PURE__*/_jsxs(Content.Header, {
        className: classnames(S.header, S.borderTop),
        children: [/*#__PURE__*/_jsx(Text, {
          variant: "sectionTitle",
          children: "Location-specific settings"
        }), /*#__PURE__*/_jsx(Text, {
          variant: "sectionBody",
          children: "These settings apply only to this location. Changes made here will not affect any other location."
        })]
      }), /*#__PURE__*/_jsxs(Content.Area, {
        className: classnames(S.area, enabledAllLocations === 'true' || !active ? S.disabled : ''),
        children: [/*#__PURE__*/_jsx(Text, {
          variant: "listItemTitle",
          children: "Enable for this location"
        }), /*#__PURE__*/_jsx(Text, {
          variant: "listItemSubtitle",
          children: "Enable to run SIM OTA on locators at this location."
        }), /*#__PURE__*/_jsxs(Flex, {
          variant: "rowL",
          className: S.paddingTop,
          children: [/*#__PURE__*/_jsx(Toggle, {
            variant: "default",
            value: enabledThisLocation,
            disabled: loadingEnabledThisLocation || enabledAllLocations === 'true' || !active,
            eventData: "enabledThisLocation",
            onChange: toggleLocation
          }), /*#__PURE__*/_jsx(Label, {
            variant: "inputCaption",
            className: S.label,
            children: enabledThisLocation ? 'Enabled for this location' : 'Disabled for this location'
          })]
        })]
      })]
    });
  };
  return /*#__PURE__*/_jsxs(Content, {
    className: S.content,
    relative: true,
    children: [/*#__PURE__*/_jsx(SystemSection, {}), /*#__PURE__*/_jsx(LocationSection, {})]
  });
};
export default SimOTACard;